import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore as ExpandMoreIcon,
  Refresh,
} from '@mui/icons-material';
import {
  QueryDocumentSnapshot,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import {
  ACTIVITY_LOG_PAGE_SIZE,
  getActivityLogQuery,
  usersCollectionRef,
} from '../../lib/firebase';
import { useFirebaseContext } from '../../lib/firebase-context';
import type { ActivityLogEntry } from '../../lib/types';
import {
  ChangesTable,
  KeyValueBlock,
  formatAbsolute,
  formatRelative,
  verbPhrase,
} from './activity-log-list.utils';

type AppUser = { ref: string; name?: string; email?: string };
type UserDocData = Omit<AppUser, 'ref'>;

const EntryRow = ({ entry }: { entry: ActivityLogEntry }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails =
    (entry.changes && Object.keys(entry.changes).length > 0) ||
    !!entry.snapshot ||
    !!entry.meta;

  return (
    <ListItem
      divider
      sx={{
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 0.75,
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
        }}
      >
        <Tooltip title={formatAbsolute(entry.timestamp)}>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ minWidth: 80, flexShrink: 0 }}
          >
            {formatRelative(entry.timestamp)}
          </Typography>
        </Tooltip>
        <Typography variant='body2' sx={{ fontWeight: 500, flexShrink: 0 }}>
          {entry.actorName || entry.actorEmail || '(unknown actor)'}
        </Typography>
        <Typography variant='body2' sx={{ flexGrow: 1 }}>
          {verbPhrase(entry)}
        </Typography>
        <Chip
          label={entry.action}
          size='small'
          variant='outlined'
          sx={{ fontSize: '0.65rem', height: 18 }}
        />
        {hasDetails && (
          <IconButton size='small' onClick={() => setExpanded((s) => !s)}>
            {expanded ? (
              <ExpandLess fontSize='small' />
            ) : (
              <ExpandMoreIcon fontSize='small' />
            )}
          </IconButton>
        )}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 11, pr: 2 }}>
          {entry.changes && Object.keys(entry.changes).length > 0 && (
            <ChangesTable changes={entry.changes} />
          )}
          {entry.snapshot && (
            <>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mt: 1, display: 'block', fontWeight: 600 }}
              >
                Snapshot
              </Typography>
              <KeyValueBlock obj={entry.snapshot} />
            </>
          )}
          {entry.meta && (
            <>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mt: 1, display: 'block', fontWeight: 600 }}
              >
                Context
              </Typography>
              <KeyValueBlock obj={entry.meta} />
            </>
          )}
        </Box>
      </Collapse>
    </ListItem>
  );
};

const ActivityLogList = () => {
  const { events } = useFirebaseContext();

  const [users, setUsers] = useState<AppUser[]>([]);
  useEffect(() => {
    const unsub = onSnapshot(usersCollectionRef, (snap) => {
      const list: AppUser[] = [];
      snap.forEach((d) =>
        list.push({ ref: d.id, ...(d.data() as UserDocData) }),
      );
      setUsers(list);
    });
    return unsub;
  }, []);

  const [filterEventRef, setFilterEventRef] = useState<string>('');
  const [filterActorUid, setFilterActorUid] = useState<string>('');

  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot | undefined>(
    undefined,
  );
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = getActivityLogQuery({
        eventRef: filterEventRef || undefined,
        actorUid: filterActorUid || undefined,
      });
      const snap = await getDocs(q);
      const docs = snap.docs;
      const mapped: ActivityLogEntry[] = docs.map((d) => ({
        ref: d.id,
        ...(d.data() as Omit<ActivityLogEntry, 'ref'>),
      }));
      setEntries(mapped);
      setCursor(docs.length > 0 ? docs[docs.length - 1] : undefined);
      setHasMore(docs.length >= ACTIVITY_LOG_PAGE_SIZE);
    } catch (err) {
      console.error('Error loading activity log', err);
      setError('Failed to load activity log.');
      setEntries([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [filterEventRef, filterActorUid]);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadMore = async () => {
    if (!cursor || loading) return;
    setLoading(true);
    setError(null);
    try {
      const q = getActivityLogQuery({
        eventRef: filterEventRef || undefined,
        actorUid: filterActorUid || undefined,
        startAfter: cursor,
      });
      const snap = await getDocs(q);
      const docs = snap.docs;
      const mapped: ActivityLogEntry[] = docs.map((d) => ({
        ref: d.id,
        ...(d.data() as Omit<ActivityLogEntry, 'ref'>),
      }));
      setEntries((prev) => [...prev, ...mapped]);
      setCursor(docs.length > 0 ? docs[docs.length - 1] : undefined);
      setHasMore(docs.length >= ACTIVITY_LOG_PAGE_SIZE);
    } catch (err) {
      console.error('Error loading next page', err);
      setError('Failed to load more entries.');
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [events]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) =>
      (a.name || a.ref).localeCompare(b.name || b.ref, undefined, {
        sensitivity: 'base',
      }),
    );
  }, [users]);

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2, alignItems: 'center' }}
      >
        <FormControl size='small' sx={{ minWidth: 200, flexGrow: 1 }}>
          <InputLabel id='filter-event-label'>Event</InputLabel>
          <Select
            labelId='filter-event-label'
            label='Event'
            value={filterEventRef}
            onChange={(e) => setFilterEventRef(e.target.value)}
          >
            <MenuItem value=''>All events</MenuItem>
            {sortedEvents.map((ev) => (
              <MenuItem key={ev.ref} value={ev.ref}>
                {ev.name}
                {ev.isActive ? ' (active)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 200, flexGrow: 1 }}>
          <InputLabel id='filter-actor-label'>Actor</InputLabel>
          <Select
            labelId='filter-actor-label'
            label='Actor'
            value={filterActorUid}
            onChange={(e) => setFilterActorUid(e.target.value)}
          >
            <MenuItem value=''>All actors</MenuItem>
            {sortedUsers.map((u) => (
              <MenuItem key={u.ref} value={u.ref}>
                {u.name || u.ref}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant='outlined'
          size='small'
          startIcon={<Refresh />}
          onClick={reload}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {filterEventRef && (
        <Alert severity='info' sx={{ mb: 2 }}>
          User and printer changes are global and only appear under &quot;All
          events&quot;.
        </Alert>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && entries.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : entries.length === 0 ? (
        <Typography color='text.secondary' textAlign='center' sx={{ py: 4 }}>
          No activity yet for this event / actor.
        </Typography>
      ) : (
        <List dense disablePadding>
          {entries.map((entry) => (
            <EntryRow key={entry.ref} entry={entry} />
          ))}
        </List>
      )}

      {entries.length > 0 && hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={loadMore} disabled={loading} variant='outlined'>
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </>
  );
};

export default ActivityLogList;
